import React, { useEffect, useRef } from "react";
import {
  List,
  DataTable,
  TextField,
  DateField,
  ReferenceField,
  TextInput,
  ReferenceInput,
  SelectInput,
  TabbedForm,
  Create,
  Edit,
  BooleanInput,
  NumberInput,
  useRecordContext,
  EditButton,
  Toolbar,
  SaveButton,
  DeleteButton,
  Button,
  useNotify,
  useGetList,
  useRefresh,
  ReferenceOneField,
  SimpleShowLayout,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  FilterButton,
  Pagination,
} from "react-admin";

import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";

import MuiButton from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MuiTextField from "@mui/material/TextField";
import MuiAutocomplete from "@mui/material/Autocomplete";

import VisibilityIcon from "@mui/icons-material//Visibility";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { httpClient } from "../lib/httpClient";
import { hasScope, SCOPE } from "../lib/auth";
import { OnlineField } from "../lib/onlineField";

import { SmartTankControls } from "./smart_tanks";
import { DetailsButton } from "../lib/detailsButton";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";

// Metadata to assist users when viewing/editing settings — hints only, no validation.
// The backend remains the source of truth; these are purely display aids.
const SETTINGS_META: Record<
  string,
  { unit?: string; discrete?: Record<number, string>; constraint?: string }
> = {
  // --- Pumpover ---
  pumpov_enable: {
    discrete: { 0: "Disabled", 1: "Solenoid", 2: "VFD (GS10)" },
  },
  pumpov_duration: { unit: "minutes" },
  pumpov_settle: { unit: "minutes" },
  pumpov_vfd_freq: { unit: "Hz" },

  // --- Redox / Sparging ---
  redox_enable: {
    discrete: {
      0: "Disabled",
      1: "Enabled",
      2: "Enabled with resistance override",
    },
  },
  sparge_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  sparge_interval: { unit: "seconds" },
  sparge_duration: { unit: "seconds" },
  sparge_setpoint: { unit: "mV" },
  massflow_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },

  // --- Temperature ---
  temp1_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  temp2_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  temp3_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  temp_source: { discrete: { 1: "temp1", 4: "redox" } },
  temp_ctl_enable: {
    discrete: {
      0: "Disabled",
      1: "Heat/Cool (separate valves)",
      2: "Heat/Cool (shared valve)",
      3: "Diverter",
    },
  },
  out1_invert: { discrete: { 0: "Normal", 1: "Inverted" } },
  temp_mode: { discrete: { 0: "Disabled", 1: "Heating", 2: "Cooling" } },
  temp_setpoint: { unit: "10ths of deg C" },
  temp_deadband: { unit: "10ths of deg C" },

  // --- Brix ---
  bb1_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  bb1_height_um: { unit: "µm" },
  bb1_interval: { unit: "minutes" },
  brix_enable: { discrete: { 0: "Disabled", 1: "Enabled" } },
  brix_height_um: { unit: "µm" },
  brix_upper_zero: { unit: "µm" },
  brix_lower_zero: { unit: "µm" },

  // --- Display / Misc ---
  disp_fahrenheit: { discrete: { 0: "Celsius", 1: "Fahrenheit" } },
  density_unit: { discrete: { 0: "SI", 1: "Brix", 2: "Oechsle" } },

  // --- Strings ---
  lot_name: { constraint: "16 chars max" },
  timezone: { constraint: "TZDATA format, 63 chars max" },
  pumpov_cron: { constraint: "6-field cron (Sec Min Hr DoM Mon DoW)" },
};

const DeviceAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span> {"Device " + (record.serial ? record.serial : record.name)}</span>
  );
};

const DeviceQRCodeImg = ({ deviceID }) => {
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const update = async () => {
      const url = "/api/v2/devices/" + deviceID + "/qr";
      try {
        const resp = await httpClient(url);
        if (resp.ok) {
          const blob = await resp.blob();
          const reader = new FileReader();
          await new Promise((resolve, reject) => {
            reader.onload = resolve;
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          if (typeof reader.result === "string") {
            imgRef.current.src = reader.result;
          }
        } else {
          console.log(resp.text);
        }
      } catch (err) {
        console.log(err);
      }
    };
    update();
  }, [deviceID, imgRef]);

  return (
    <img
      ref={imgRef}
      src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
      alt=""
      style={{ height: 256, width: 256 }}
    />
  );
};

const DeviceQRCode = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <div
      style={{
        background: "white",
        filter: "drop-shadow(0 0 0.5rem #ccc)",
        padding: 10,
        paddingBottom: 20,
        margin: 10,
        borderRadius: 15,
      }}
    >
      <DeviceQRCodeImg deviceID={record.id} />
      <div style={{ textAlign: "center", fontWeight: "bold" }}>
        FS1 {record.serial ? record.serial : record.name}
      </div>
    </div>
  );
};

const ObjectDisplayValue = ({ obj, objKey }) => {
  const [show, setShow] = React.useState<boolean>(false);

  if (objKey === "mqtt_password" || objKey === "wifiPassword") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={show ? {} : { filter: "blur(4px)" }}>{obj[objKey]}</span>
        <IconButton
          aria-label="show"
          size="small"
          onClick={() => {
            setShow(!show);
          }}
        >
          <VisibilityIcon fontSize="inherit" />
        </IconButton>
      </div>
    );
  }
  return obj[objKey];
};

const objectToRows = (obj) => {
  if (!obj) return null;

  return Object.keys(obj)
    .sort()
    .map((key) => (
      <TableRow key={key}>
        <TableCell>{key}</TableCell>
        <TableCell>
          <ObjectDisplayValue obj={obj} objKey={key} />
        </TableCell>
      </TableRow>
    ));
};

const DevicePropertiesTable = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Table size="small" style={{ width: "inherit" }}>
      <TableBody>
        <TableRow key="online">
          <TableCell>Online</TableCell>
          <TableCell>{record.online ? "yes" : "no"}</TableCell>
        </TableRow>
        <TableRow key="lastSeen">
          <TableCell>Last Online</TableCell>
          <TableCell>{record.lastSeen}</TableCell>
        </TableRow>
        {objectToRows(record.properties)}
      </TableBody>
    </Table>
  );
};

const getSettingUnits = (key: string): string => {
  const meta = SETTINGS_META[key];
  if (!meta) return "";
  const parts: string[] = [];
  if (meta.unit) parts.push(meta.unit);
  if (meta.constraint) parts.push(meta.constraint);
  return parts.join(", ");
};

const DeviceSettingsTable = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (!record.settings || Object.keys(record.settings).length === 0) {
    return (
      <Alert severity="info">
        No settings have been reported by this device
      </Alert>
    );
  }

  return (
    <Table size="small" style={{ width: "inherit" }}>
      <TableBody>
        {Object.keys(record.settings)
          .sort()
          .map((key) => (
            <TableRow key={key}>
              <TableCell>{key}</TableCell>
              <TableCell>
                <ObjectDisplayValue obj={record.settings} objKey={key} />
              </TableCell>
              <TableCell style={{ color: "#666", fontStyle: "italic" }}>
                {getSettingUnits(key)}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

const DeviceRecoveryTab = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (!record.recovery) {
    return <Alert severity="info">Device is not in recovery mode</Alert>;
  }

  return (
    <>
      <DeviceProvisionButton />
      <Table size="small" style={{ width: "inherit" }}>
        <TableBody>{objectToRows(record.recovery)}</TableBody>
      </Table>
    </>
  );
};
const getDeviceFilters = (forCustomer: boolean) => {
  let filters = [
    <BooleanInput source="online" label="Online" />,
    <TextInput source="firmwareName" label="Firmware Name" />,
  ];

  if (!forCustomer) {
    filters.push(
      <ReferenceInput
        source="customerID"
        reference="customers"
        label="Customer"
      >
        <SelectInput optionText="longName" label="Customer" />
      </ReferenceInput>,
    );
  }

  return filters;
};

export const DeviceList = ({ forCustomer }) => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={getDeviceFilters(forCustomer)}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton disabled={!hasScope(SCOPE.ADMIN)} />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="id" label="ID" disableSort>
        <TextField source="id" label="ID" />
      </DataTable.Col>
      <DataTable.Col source="name" label="Name" disableSort>
        <TextField source="name" label="Name" />
      </DataTable.Col>
      <DataTable.Col source="serial" label="Serial" disableSort>
        <TextField source="serial" label="Serial" />
      </DataTable.Col>

      {forCustomer ? undefined : (
        <DataTable.Col source="customerID" label="Customer" disableSort>
          <ReferenceField
            source="customerID"
            reference="customers"
            sortable={false}
          >
            <TextField source="longName" />
          </ReferenceField>
        </DataTable.Col>
      )}

      <DataTable.Col source="online" label="Online" disableSort>
        <OnlineField label="Online" />
      </DataTable.Col>
      <DataTable.Col
        source="properties.firmwareName"
        label="FW Name"
        disableSort
      >
        <TextField source="properties.firmwareName" label="FW Name" />
      </DataTable.Col>
      <DataTable.Col
        source="properties.firmwareVersion"
        label="FW Version"
        disableSort
      >
        <TextField source="properties.firmwareVersion" label="FW Version" />
      </DataTable.Col>
      <DataTable.Col source="lastSeen" label="Last Seen" disableSort>
        <DateField source="lastSeen" label="Last Seen" />
      </DataTable.Col>
      <DataTable.Col>
        <EditButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const DeviceProvisionButton = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  const deviceDescription = record.serial || "Device ID " + record.id;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };
  const handleProvision = () => {
    setRunning(true);
    httpClient(
      "/api/v2/c/" + record.customerID + "/devices/" + record.id + "/provision",
      {
        method: "POST",
      },
    )
      .then(async (resp) => {
        if (resp.ok) {
          notify(deviceDescription + " provisioned", {
            type: "success",
          });
        } else {
          notify(deviceDescription + " provision failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(deviceDescription + " provision failed", {
          type: "error",
        });
        console.log(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  return (
    <>
      <Button label="Provision" size="medium" onClick={handleOpen}>
        <RotateLeftIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm Device Provision</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to provision <b>{deviceDescription}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleProvision} disabled={running}>
            Provision
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DeviceRebootButton = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  const deviceDescription = record.serial || "Device ID " + record.id;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };
  const handleReboot = () => {
    setRunning(true);
    httpClient(
      "/api/v2/c/" + record.customerID + "/devices/" + record.id + "/reboot",
      {
        method: "POST",
      },
    )
      .then(async (resp) => {
        if (resp.ok) {
          notify(deviceDescription + " rebooted", {
            type: "success",
          });
        } else {
          notify(deviceDescription + " reboot failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(deviceDescription + " reboot failed", { type: "error" });
        console.log(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  return (
    <>
      <Button
        label="Reboot"
        size="medium"
        disabled={!record.online}
        onClick={handleOpen}
      >
        <RotateLeftIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm Reboot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reboot <b>{deviceDescription}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleReboot} disabled={running}>
            Reboot
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FirmwaresAutocomplete = ({ onSelectionChange, firmwareName }) => {
  const { data, isLoading, error } = useGetList("firmware", {
    filter: { name: firmwareName },
  });

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error</p>;
  if (!data) return null;

  const onChange = (_event, value) => {
    if (value) {
      onSelectionChange(value.name);
    } else {
      onSelectionChange(undefined);
    }
  };

  return (
    <MuiAutocomplete
      options={data.sort((b, a) => a.name.localeCompare(b.name))}
      getOptionLabel={(obj) => obj.name}
      renderInput={(params) => <MuiTextField {...params} label="Firmware" />}
      onChange={onChange}
    />
  );
};

const DeviceOTAButton = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);
  const [firmwareBin, setFirmwareBin] = React.useState<boolean>();

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  const deviceDescription = record.serial || "Device ID " + record.id;

  const handleOpen = () => {
    setOpen(true);
    setRunning(false);
    setFirmwareBin(undefined);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const handleOTA = () => {
    setRunning(true);
    httpClient(
      "/api/v2/c/" + record.customerID + "/devices/" + record.id + "/ota",
      {
        method: "POST",
        body: JSON.stringify({ firmware: firmwareBin }),
      },
    )
      .then(async (resp) => {
        if (resp.ok) {
          notify(deviceDescription + " OTA initiated", {
            type: "success",
          });
        } else {
          notify(deviceDescription + " OTA failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(deviceDescription + " OTA failed", { type: "error" });
        console.log(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  const handleScheduleOTA = () => {
    record.internal.targetFirmware = firmwareBin;

    setRunning(true);
    httpClient("/api/v2/c/" + record.customerID + "/devices/" + record.id, {
      method: "PUT",
      body: JSON.stringify(record),
    })
      .then(async (resp) => {
        if (resp.ok) {
          notify(deviceDescription + " OTA scheduled", {
            type: "success",
          });
        } else {
          notify(deviceDescription + " OTA schedule failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(deviceDescription + " internal setting change failed", {
          type: "error",
        });
        console.log(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  return (
    <>
      <Button label="OTA" size="medium" onClick={handleOpen}>
        <SettingsInputAntennaIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm OTA Firmware Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            What firmware do you want to send to <b>{deviceDescription}</b>?
          </DialogContentText>
          <DialogContentText style={{ fontSize: "70%" }}>
            Currently running:{" "}
            <b>
              {record.properties.firmwareName}-
              {record.properties.firmwareVersion}
            </b>
          </DialogContentText>
          <FirmwaresAutocomplete
            onSelectionChange={setFirmwareBin}
            firmwareName={record.properties.firmwareName}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleOTA}
            disabled={!firmwareBin || running || !record.online}
          >
            Initiate
          </MuiButton>
          <MuiButton
            onClick={handleScheduleOTA}
            disabled={!firmwareBin || running}
          >
            Schedule
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const knownSettingKeys = Object.keys(SETTINGS_META);

const SettingsFieldAutocomplete = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const [inputValue, setInputValue] = React.useState(value);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <MuiAutocomplete
      value={inputValue}
      onChange={(_event, newValue) => {
        if (typeof newValue === "string") {
          setInputValue(newValue);
          onChange(newValue);
        }
      }}
      onInputChange={(_event, newInput) => {
        setInputValue(newInput);
        onChange(newInput);
      }}
      options={knownSettingKeys}
      freeSolo
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label="Field"
          placeholder="Type or pick a setting…"
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {option}
        </li>
      )}
    />
  );
};

function isJsonValid(jsonString) {
  try {
    JSON.parse(jsonString);
    return true;
  } catch (error) {
    return false;
  }
}

const DeviceSettingsButton = () => {
  const refresh = useRefresh();
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);
  const [settingField, setSettingField] = React.useState<string>();
  const [settingValue, setSettingValue] = React.useState<string>();
  const [error, setError] = React.useState<string>("");

  const notify = useNotify();
  const record = useRecordContext();
  if (!record) return null;

  const deviceDescription = record.serial || "Device ID " + record.id;

  const handleOpen = () => {
    setOpen(true);
    setRunning(false);
    setSettingField(undefined);
    setSettingValue(undefined);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const handleSettings = () => {
    var payload = {};
    payload[settingField] = JSON.parse(settingValue);
    setRunning(true);
    httpClient(
      "/api/v2/c/" + record.customerID + "/devices/" + record.id + "/settings",
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    )
      .then(async (resp) => {
        if (resp.ok) {
          notify(deviceDescription + " setting change initiated", {
            type: "success",
          });
        } else {
          notify(deviceDescription + " setting change failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        notify(deviceDescription + " setting change failed", {
          type: "error",
        });
        console.log(error);
      })
      .finally(() => {
        setOpen(false);
        setRunning(false);
        refresh();
      });
  };

  const validateCron = (value) => {
    if (typeof value !== "string") return false;
    if (value === "") return true;

    // Split into fields (should be 6 fields)
    const fields = value.split(/\s+/);
    if (fields.length !== 6) return false;

    return true;
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    if (value.trim() === "") {
      setError("Value cannot be empty");
    } else if (!isJsonValid(value)) {
      setError('Value must be a valid JSON (e.g. "string", false, 1.3)');
    } else if (
      settingField.endsWith("_cron") &&
      !validateCron(JSON.parse(value))
    ) {
      setError("Cron string must be 6 fields: Sec Min Hr DoM Mon DoW");
    } else {
      setError("");
      setSettingValue(value);
    }
  };

  const settingHint = (() => {
    const meta = SETTINGS_META[settingField];
    if (!meta) return null;
    const parts: string[] = [];
    if (meta.unit) parts.push(`Unit: ${meta.unit}`);
    if (meta.discrete) {
      const labels = Object.entries(meta.discrete)
        .map(([k, v]) => `${k}=${v}`)
        .join(", ");
      parts.push(`Values: ${labels}`);
    }
    if (meta.constraint) parts.push(`Constraint: ${meta.constraint}`);
    return parts.length > 0 ? parts.join(" | ") : null;
  })();

  return (
    <>
      <Button
        label="Settings"
        size="medium"
        disabled={!record.online}
        onClick={handleOpen}
      >
        <SettingsSuggestIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm Settings Field Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            What settings field do you want to change on{" "}
            <b>{deviceDescription}</b>?
          </DialogContentText>
          <SettingsFieldAutocomplete
            value={settingField ?? ""}
            onChange={(v) => setSettingField(v)}
          />
          <MuiTextField
            autoFocus
            fullWidth
            label="Value"
            onChange={handleValueChange}
            error={!!error}
            helperText={
              settingHint
                ? settingHint + (error ? "  •  " + error : " ")
                : error
            }
          />
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleSettings}
            disabled={!settingField || !settingValue || running}
          >
            Send
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DeviceEditToolbar = ({ forCustomer }) => (
  <Toolbar style={{ flex: 1, justifyContent: "space-between" }}>
    <SaveButton label="Save" />
    <DeviceSettingsButton />
    <DeviceOTAButton />
    <DeviceRebootButton />
    <DeleteButton
      size="medium"
      disabled={!hasScope(SCOPE.ADMIN) || forCustomer}
    />
  </Toolbar>
);

const CreateEditForm = ({ forCustomer, ...props }) => {
  const rec = useRecordContext();

  return (
    <TabbedForm
      toolbar=<DeviceEditToolbar forCustomer={forCustomer} />
      {...props}
    >
      <TabbedForm.Tab label="General">
        <NumberInput label="ID" source="id" readOnly />
        <TextInput source="name" readOnly={!hasScope(SCOPE.ADMIN)} />
        <TextInput source="serial" readOnly={!hasScope(SCOPE.ADMIN)} />
        {forCustomer ? undefined : (
          <ReferenceInput source="customerID" reference="customers">
            <SelectInput optionText="longName" label="Customer" />
          </ReferenceInput>
        )}
        <TextInput label="Target Firmware" source="internal.targetFirmware" />
      </TabbedForm.Tab>
      {forCustomer &&
      rec &&
      rec.name.startsWith("cm13-") &&
      hasScope(SCOPE.ADMIN) ? (
        <TabbedForm.Tab label="Smart Tank">
          <ReferenceOneField
            reference="smart_tanks"
            target="deviceID"
            link={false}
            empty="No Smart Tank Association"
          >
            <SimpleShowLayout>
              <DetailsButton />
              <TextField source="id" label="Smart Tank ID" />
              <AliasOrBarcodeReference
                source="vesselID"
                reference="vessels"
                label="Vessel"
              />
              <ReferenceField
                label="Lot"
                source="lotID"
                reference="lots"
                link="show"
                empty="None"
              >
                <TextField source="name" />
              </ReferenceField>
              <SmartTankControls />
            </SimpleShowLayout>
          </ReferenceOneField>
        </TabbedForm.Tab>
      ) : undefined}
      {rec && rec.name.startsWith("fs1-") && hasScope(SCOPE.ADMIN) ? (
        <TabbedForm.Tab label="Calibration">
          <NumberInput
            label="Calibration Offset"
            source="internal.calibrationOffset"
            helperText="Offset to absorbance value prior to applying calibration (e.g.: -0.012)"
          />
          <NumberInput
            label="Red Calibration"
            source="internal.calibrationRed"
          />
          <NumberInput
            label="White Calibration"
            source="internal.calibrationWhite"
          />
        </TabbedForm.Tab>
      ) : undefined}
      {rec && rec.name.startsWith("LWN-") && hasScope(SCOPE.ADMIN) ? (
        <TabbedForm.Tab label="Water Use">
          <NumberInput
            label="Litres Per Pulse"
            source="internal.litresPerPulse"
          />
          <TextInput
            label="Default Category"
            source="internal.defaultCategory"
          />
        </TabbedForm.Tab>
      ) : undefined}
      <TabbedForm.Tab label="Status">
        <DevicePropertiesTable />
      </TabbedForm.Tab>
      <TabbedForm.Tab label="Settings">
        <DeviceSettingsTable />
      </TabbedForm.Tab>
      {rec && rec.name.startsWith("fs1-") && hasScope(SCOPE.ADMIN) ? (
        <TabbedForm.Tab label="QR">
          <DeviceQRCode />
        </TabbedForm.Tab>
      ) : undefined}
      <TabbedForm.Tab label="Recovery">
        <DeviceRecoveryTab />
      </TabbedForm.Tab>
    </TabbedForm>
  );
};

export const DeviceEdit = ({ forCustomer }) => (
  <Edit title={<DeviceAppBarTitle />}>
    <CreateEditForm redirect={false} forCustomer={forCustomer} />
  </Edit>
);

export const DeviceCreate = () => (
  <Create>
    <CreateEditForm forCustomer={false} />
  </Create>
);
